import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, CheckCircle2 } from "lucide-react";

const uid = () => "sess_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);

export default function AiQuoteDialog({ open, onOpenChange, initialPrompt }) {
  const [sessionId] = useState(uid);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [ready, setReady] = useState(null);
  const [finalized, setFinalized] = useState(null);
  const scrollRef = useRef(null);
  const autoSentRef = useRef(false);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: "Ciao! Sono l'assistente di Energy World. Descrivimi in poche parole di cosa hai bisogno e ti aiuterò a preparare il tuo preventivo." }]);
    }
  }, [open, messages.length]);

  // Auto-send the initial prompt (e.g. from offer popup) once, right after the greeting
  useEffect(() => {
    if (!open || !initialPrompt || autoSentRef.current) return;
    if (messages.length !== 1) return; // wait for greeting to be present
    autoSentRef.current = true;
    (async () => {
      setMessages(m => [...m, { role: "user", content: initialPrompt }]);
      setSending(true);
      try {
        const r = await api.post("/quotes/chat", { session_id: sessionId, message: initialPrompt });
        setMessages(m => [...m, { role: "assistant", content: r.data.reply }]);
        if (r.data.ready) setReady(r.data.ready);
      } catch { toast.error("Errore comunicazione IA"); }
      finally { setSending(false); }
    })();
    // eslint-disable-next-line
  }, [open, initialPrompt, messages.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    setMessages(m => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const r = await api.post("/quotes/chat", { session_id: sessionId, message: text });
      setMessages(m => [...m, { role: "assistant", content: r.data.reply }]);
      if (r.data.ready) setReady(r.data.ready);
    } catch (e) {
      toast.error("Errore comunicazione IA");
    } finally {
      setSending(false);
    }
  };

  const confirmSend = async () => {
    if (!ready) return;
    setSending(true);
    try {
      const r = await api.post("/quotes/finalize", {
        session_id: sessionId,
        category: ready.categoria || ready.category || "Altro",
        city: ready.city || "",
        name: ready.name || "",
        email: ready.email || null,
        phone: ready.phone || null,
        summary: ready.summary || "",
      });
      setFinalized(r.data);
      toast.success("Richiesta inviata!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[80vh] flex flex-col p-0 bg-white" data-testid="ai-quote-dialog">
        <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-blue-800 to-indigo-600 rounded-t-lg">
          <DialogTitle className="text-white flex items-center gap-2 heading text-xl">
            <Sparkles className="w-5 h-5" /> Preventivo semplice con l'IA
          </DialogTitle>
          <DialogDescription className="text-blue-100 text-sm">Rispondi alle domande e prepariamo tutto noi</DialogDescription>
        </DialogHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-3 bg-slate-50" data-testid="ai-chat-scroll">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-3 rounded-lg whitespace-pre-wrap text-sm leading-relaxed ${
                m.role === "user" ? "bg-blue-800 text-white" : "bg-white border border-slate-200 text-slate-800"
              }`}>
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="px-4 py-3 bg-white border border-slate-200 rounded-lg">
                <Loader2 className="w-4 h-4 animate-spin text-blue-800" />
              </div>
            </div>
          )}

          {ready && !finalized && (
            <div className="border-2 border-amber-400 bg-amber-50 rounded-lg p-4 mt-4" data-testid="ai-quote-ready">
              <div className="font-bold text-amber-900 mb-2">Riepilogo richiesta</div>
              <ul className="text-sm text-slate-800 space-y-1">
                <li><b>Categoria:</b> {ready.categoria || ready.category}</li>
                <li><b>Città:</b> {ready.city}</li>
                <li><b>Nome:</b> {ready.name}</li>
                {ready.email && <li><b>Email:</b> {ready.email}</li>}
                {ready.phone && <li><b>Telefono:</b> {ready.phone}</li>}
                <li><b>Note:</b> {ready.summary}</li>
              </ul>
              <Button onClick={confirmSend} disabled={sending} className="mt-3 w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="ai-quote-confirm-btn">
                Conferma e invia richiesta
              </Button>
            </div>
          )}

          {finalized && (
            <div className="border-2 border-emerald-400 bg-emerald-50 rounded-lg p-4 mt-4 text-center" data-testid="ai-quote-success">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
              <div className="font-bold text-emerald-900">Richiesta inviata!</div>
              <div className="text-sm text-slate-700 mt-1">Riferimento: <b>{finalized.quote_id}</b></div>
              <div className="text-sm text-slate-600 mt-1">Ti contatteremo il prima possibile.</div>
            </div>
          )}
        </div>

        {!finalized && (
          <div className="p-4 border-t bg-white flex gap-2">
            <Input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && send()}
              placeholder="Scrivi il tuo messaggio..."
              disabled={sending || !!ready}
              data-testid="ai-chat-input"
              className="flex-1"
            />
            <Button onClick={send} disabled={sending || !input.trim() || !!ready} data-testid="ai-chat-send" aria-label="Invia" className="bg-blue-800 hover:bg-blue-900">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
